import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, Link, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Brain, ArrowLeft, Upload, Mic, MicOff, LinkIcon, Play, Loader2,
  FileAudio, X, BarChart3, MessageSquare, Target, ListChecks, Star,
  Download, Trash2, Clock, AlertTriangle, ChevronDown, ChevronUp,
  Phone, TrendingUp, ArrowRight, History, Sparkles, LogIn, LogOut, FileDown, Check,
  Heart, ShieldCheck, Volume2, AlertOctagon
} from "lucide-react";
import SubscriptionPlans from "../components/SubscriptionPlans";
import { loginWithGoogle as startGoogleLogin } from "../lib/userAuth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } })
};

/* ─── Sentiment Color ─── */
function sentimentColor(s) {
  if (!s) return "text-zinc-400";
  const map = { positive: "text-emerald-400", negative: "text-red-400", neutral: "text-zinc-400", mixed: "text-amber-400" };
  return map[s] || "text-zinc-400";
}
function sentimentBg(s) {
  const map = { positive: "bg-emerald-500/15", negative: "bg-red-500/15", neutral: "bg-zinc-500/15", mixed: "bg-amber-500/15" };
  return map[s] || "bg-zinc-500/15";
}

/* ─── Score Ring ─── */
function ScoreRing({ score, label, size = 64 }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const color = score >= 70 ? "#00F0FF" : score >= 40 ? "#f59e0b" : "#ef4444";
  return (
    <div className="flex flex-col items-center gap-1.5">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#27272A" strokeWidth="4" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="4"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1s ease" }} />
      </svg>
      <span className="text-white font-bold text-sm" style={{ marginTop: -(size/2 + 8) }}>{score}</span>
      <span className="text-zinc-500 text-[10px] mt-3">{label}</span>
    </div>
  );
}

/* ─── Audio Recorder Hook ─── */
function useAudioRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [duration, setDuration] = useState(0);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const timerRef = useRef(null);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunks.current = [];
      mediaRecorder.current.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
      mediaRecorder.current.onstop = () => {
        const blob = new Blob(chunks.current, { type: "audio/webm" });
        setAudioBlob(blob);
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRecorder.current.start();
      setIsRecording(true);
      setDuration(0);
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    } catch (err) {
      console.error("Mic access denied:", err);
      alert("Microphone access is required for recording.");
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  }, [isRecording]);

  const clearRecording = useCallback(() => {
    setAudioBlob(null);
    setDuration(0);
  }, []);

  return { isRecording, audioBlob, duration, startRecording, stopRecording, clearRecording };
}

/* ─── Results Dashboard ─── */
function AnalysisResults({ data }) {
  const [activeTab, setActiveTab] = useState("transcript");
  const a = data.analysis || {};

  const tabs = [
    { id: "transcript", label: "Transcript", icon: MessageSquare },
    { id: "sentiment", label: "Sentiment", icon: BarChart3 },
    { id: "empathy", label: "Empathy", icon: Heart },
    { id: "resolution", label: "Resolution", icon: ShieldCheck },
    { id: "dead_air", label: "Dead Air", icon: Volume2 },
    { id: "behavioral", label: "Behavioral", icon: TrendingUp },
    { id: "topics", label: "Topics", icon: Target },
    { id: "actions", label: "Actions", icon: ListChecks },
    { id: "agent", label: "Agent", icon: Star },
  ];

  const riskColor = (level) => {
    const map = { low: "text-emerald-400", medium: "text-amber-400", high: "text-orange-400", critical: "text-red-400" };
    return map[level] || "text-zinc-400";
  };
  const riskBg = (level) => {
    const map = { low: "bg-emerald-500/15", medium: "bg-amber-500/15", high: "bg-orange-500/15", critical: "bg-red-500/15" };
    return map[level] || "bg-zinc-500/15";
  };

  /* Mini line chart for sentiment journey */
  const SentimentJourney = ({ journey }) => {
    if (!journey || journey.length === 0) return <p className="text-zinc-500 text-xs">No journey data</p>;
    const maxS = 100;
    const w = 100 / journey.length;
    return (
      <div className="mt-2">
        <svg viewBox="0 0 500 120" className="w-full h-28">
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map(y => (
            <line key={y} x1="0" y1={110 - y * 1.1} x2="500" y2={110 - y * 1.1} stroke="#27272A" strokeWidth="0.5" />
          ))}
          {/* Customer line */}
          <polyline
            fill="none" stroke="#00F0FF" strokeWidth="2.5" strokeLinejoin="round"
            points={journey.map((p, i) => `${i * (500 / (journey.length - 1 || 1))},${110 - (p.customer_score || 50) * 1.1}`).join(" ")}
          />
          {/* Agent line */}
          <polyline
            fill="none" stroke="#7000FF" strokeWidth="2.5" strokeLinejoin="round" strokeDasharray="6,3"
            points={journey.map((p, i) => `${i * (500 / (journey.length - 1 || 1))},${110 - (p.agent_score || 50) * 1.1}`).join(" ")}
          />
          {/* Dots */}
          {journey.map((p, i) => (
            <g key={i}>
              <circle cx={i * (500 / (journey.length - 1 || 1))} cy={110 - (p.customer_score || 50) * 1.1} r="4" fill="#00F0FF" />
              <circle cx={i * (500 / (journey.length - 1 || 1))} cy={110 - (p.agent_score || 50) * 1.1} r="4" fill="#7000FF" />
            </g>
          ))}
        </svg>
        <div className="flex justify-between text-[9px] text-zinc-600 mt-1 px-1">
          {journey.map((p, i) => <span key={i}>{p.phase}</span>)}
        </div>
        <div className="flex gap-4 mt-2">
          <span className="text-[10px] text-[#00F0FF] flex items-center gap-1"><span className="w-3 h-0.5 bg-[#00F0FF] inline-block" /> Customer</span>
          <span className="text-[10px] text-[#7000FF] flex items-center gap-1"><span className="w-3 h-0.5 bg-[#7000FF] inline-block border-dashed" /> Agent</span>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-[#0d0d14] border border-zinc-800/60 rounded-2xl overflow-hidden" data-testid="analysis-results">
      {/* Summary */}
      <div className="p-5 border-b border-zinc-800/40">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-white font-bold text-lg font-outfit">Analysis Complete</h3>
            <p className="text-zinc-500 text-xs mt-1">{data.filename} &middot; {data.created_at?.split("T")[0]}</p>
          </div>
          <div className="flex items-center gap-2">
            <a href={`${API}/call-analyzer/report/${data.analysis_id}/pdf`} className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-[#00F0FF] to-[#7000FF] text-black text-xs font-bold rounded-lg hover:opacity-90 transition-opacity" data-testid="download-pdf">
              <FileDown size={14} /> PDF Report
            </a>
            <a href={`${API}/call-analyzer/report/${data.analysis_id}/json`} className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 text-zinc-300 text-xs rounded-lg hover:bg-zinc-700 transition-colors" data-testid="download-report">
              <Download size={14} /> JSON
            </a>
          </div>
        </div>
        {a.summary && <p className="text-zinc-300 text-sm leading-relaxed">{a.summary}</p>}

        {/* Quick stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          <div className={`px-3 py-2 rounded-lg ${sentimentBg(a.sentiment?.overall)}`}>
            <p className="text-zinc-500 text-[10px] uppercase tracking-wider">Overall</p>
            <p className={`font-bold text-sm capitalize ${sentimentColor(a.sentiment?.overall)}`}>{a.sentiment?.overall || "N/A"}</p>
          </div>
          <div className="px-3 py-2 rounded-lg bg-[#00F0FF]/10">
            <p className="text-zinc-500 text-[10px] uppercase tracking-wider">Score</p>
            <p className="font-bold text-sm text-[#00F0FF]">{a.sentiment?.score ?? "N/A"}/100</p>
          </div>
          <div className={`px-3 py-2 rounded-lg ${riskBg(a.sentiment?.escalation_risk?.level)}`}>
            <p className="text-zinc-500 text-[10px] uppercase tracking-wider">Escalation Risk</p>
            <p className={`font-bold text-sm capitalize ${riskColor(a.sentiment?.escalation_risk?.level)}`}>{a.sentiment?.escalation_risk?.level || "N/A"}</p>
          </div>
          <div className="px-3 py-2 rounded-lg bg-amber-500/10">
            <p className="text-zinc-500 text-[10px] uppercase tracking-wider">Actions</p>
            <p className="font-bold text-sm text-amber-400">{a.action_items?.length || 0}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-zinc-800/40 overflow-x-auto">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-3 text-[11px] font-medium whitespace-nowrap transition-colors border-b-2 ${activeTab === tab.id ? "text-[#00F0FF] border-[#00F0FF]" : "text-zinc-500 border-transparent hover:text-zinc-300"}`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={13} /> {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-5 max-h-[550px] overflow-y-auto">

        {/* ── Enhanced Transcript ── */}
        {activeTab === "transcript" && (
          <div className="space-y-4">
            {/* Critical Moments Jump Bar */}
            {data.critical_moments?.length > 0 && (
              <div className="bg-zinc-900/50 rounded-xl p-3">
                <p className="text-zinc-400 text-xs font-medium mb-2">Critical Moments</p>
                <div className="flex flex-wrap gap-1.5">
                  {data.critical_moments.map((cm, i) => {
                    const idx = cm.segment_index;
                    const typeIcons = { anger_spike: "text-red-400", silence: "text-zinc-400", interruption: "text-amber-400", escalation: "text-orange-400", resolution: "text-emerald-400", key_complaint: "text-red-300", positive_turn: "text-emerald-300" };
                    const typeBgs = { anger_spike: "bg-red-500/15 border-red-500/20", silence: "bg-zinc-700/50 border-zinc-600", interruption: "bg-amber-500/15 border-amber-500/20", escalation: "bg-orange-500/15 border-orange-500/20", resolution: "bg-emerald-500/15 border-emerald-500/20", key_complaint: "bg-red-500/10 border-red-500/15", positive_turn: "bg-emerald-500/10 border-emerald-500/15" };
                    return (
                      <button
                        key={i}
                        onClick={() => {
                          const el = document.getElementById(`seg-${idx}`);
                          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                        }}
                        className={`text-[10px] px-2.5 py-1 rounded-full border flex items-center gap-1 hover:opacity-80 transition-opacity ${typeBgs[cm.type] || "bg-zinc-800 border-zinc-700"}`}
                        data-testid={`critical-moment-${i}`}
                      >
                        <AlertOctagon size={10} className={typeIcons[cm.type] || "text-zinc-400"} />
                        <span className={typeIcons[cm.type] || "text-zinc-400"}>{cm.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Audio Player */}
            {data.has_audio && (
              <div className="bg-zinc-900/50 rounded-xl p-3">
                <audio
                  id="call-audio-player"
                  src={`${API}/call-analyzer/audio/${data.analysis_id}`}
                  controls
                  preload="metadata"
                  className="w-full h-8"
                  style={{ filter: "invert(1) hue-rotate(180deg)" }}
                />
              </div>
            )}

            {/* Transcript Segments */}
            <div className="space-y-1">
              {data.segments && data.segments.length > 0 ? (
                data.segments.map((seg, i) => {
                  const isAgent = seg.speaker === "agent";
                  const isCritical = seg.is_critical || data.critical_moments?.some(cm => cm.segment_index === i);
                  const emotion = seg.emotion;
                  const emotionColors = { frustrated: "border-l-red-400", angry: "border-l-red-500", confused: "border-l-amber-400", anxious: "border-l-amber-500", satisfied: "border-l-emerald-400", happy: "border-l-emerald-500", neutral: "border-l-zinc-700", disappointed: "border-l-orange-400" };

                  const highlightKeywords = (text, keywords) => {
                    if (!keywords || keywords.length === 0) return text;
                    let result = text;
                    const parts = [];
                    let lastIdx = 0;
                    const lowerText = text.toLowerCase();

                    const sortedKw = [...keywords].sort((a, b) => b.length - a.length);
                    const matches = [];
                    for (const kw of sortedKw) {
                      let searchIdx = 0;
                      while (searchIdx < lowerText.length) {
                        const found = lowerText.indexOf(kw.toLowerCase(), searchIdx);
                        if (found === -1) break;
                        matches.push({ start: found, end: found + kw.length, kw });
                        searchIdx = found + 1;
                      }
                    }
                    matches.sort((a, b) => a.start - b.start);

                    // Remove overlaps
                    const filtered = [];
                    for (const m of matches) {
                      if (filtered.length === 0 || m.start >= filtered[filtered.length - 1].end) {
                        filtered.push(m);
                      }
                    }

                    if (filtered.length === 0) return text;

                    const elements = [];
                    let pos = 0;
                    for (const m of filtered) {
                      if (m.start > pos) elements.push(text.slice(pos, m.start));
                      elements.push(`<mark class="bg-[#00F0FF]/20 text-[#00F0FF] px-0.5 rounded">${text.slice(m.start, m.end)}</mark>`);
                      pos = m.end;
                    }
                    if (pos < text.length) elements.push(text.slice(pos));
                    return elements.join("");
                  };

                  const playSegment = () => {
                    const audio = document.getElementById("call-audio-player");
                    if (audio && seg.start != null) {
                      audio.currentTime = seg.start;
                      audio.play();
                    }
                  };

                  return (
                    <div
                      key={i}
                      id={`seg-${i}`}
                      className={`flex gap-2 p-2.5 rounded-lg border-l-2 transition-colors hover:bg-zinc-900/50 ${emotionColors[emotion] || "border-l-zinc-800"} ${isCritical ? "bg-red-500/5 ring-1 ring-red-500/10" : ""}`}
                      data-testid={`transcript-seg-${i}`}
                    >
                      {/* Timestamp + Play */}
                      <div className="flex flex-col items-center gap-0.5 w-16 flex-shrink-0">
                        <button
                          onClick={playSegment}
                          className="text-[10px] font-mono text-[#00F0FF] hover:text-white transition-colors cursor-pointer"
                          title="Click to play from here"
                          data-testid={`play-seg-${i}`}
                        >
                          {seg.start}s
                        </button>
                        {data.has_audio && (
                          <Play size={10} className="text-zinc-600 hover:text-[#00F0FF] cursor-pointer" onClick={playSegment} />
                        )}
                      </div>

                      {/* Speaker Label */}
                      <div className="w-16 flex-shrink-0 pt-0.5">
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${isAgent ? "bg-[#7000FF]/15 text-[#7000FF]" : "bg-[#00F0FF]/15 text-[#00F0FF]"}`}>
                          {seg.speaker === "agent" ? "Agent" : seg.speaker === "customer" ? "Customer" : ""}
                        </span>
                      </div>

                      {/* Text with highlighted keywords */}
                      <div className="flex-1 min-w-0">
                        <p className="text-zinc-300 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightKeywords(seg.text, seg.keywords) }} />
                        {/* Emotion badge */}
                        {emotion && emotion !== "neutral" && (
                          <span className={`inline-block mt-1 text-[9px] px-1.5 py-0.5 rounded-full ${
                            ["frustrated", "angry", "disappointed"].includes(emotion) ? "bg-red-500/10 text-red-400" :
                            ["confused", "anxious"].includes(emotion) ? "bg-amber-500/10 text-amber-400" :
                            "bg-emerald-500/10 text-emerald-400"
                          }`}>{emotion}</span>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap">{data.transcript}</p>
              )}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-3 pt-3 border-t border-zinc-800/40">
              <span className="text-[9px] text-zinc-500 flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#7000FF]/40 inline-block" /> Agent</span>
              <span className="text-[9px] text-zinc-500 flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#00F0FF]/40 inline-block" /> Customer</span>
              <span className="text-[9px] text-zinc-500 flex items-center gap-1"><span className="w-3 h-0.5 bg-[#00F0FF]/30 inline-block rounded" /> Keywords</span>
              <span className="text-[9px] text-zinc-500 flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-500/20 inline-block" /> Critical</span>
            </div>
          </div>
        )}

        {/* ── Enhanced Sentiment ── */}
        {activeTab === "sentiment" && (
          <div className="space-y-5">
            {/* Customer Emotion + Agent Tone */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-zinc-900/50 rounded-xl p-3">
                <p className="text-zinc-500 text-[10px] uppercase mb-1">Customer Emotion</p>
                <p className={`font-bold text-sm capitalize ${sentimentColor(a.sentiment?.customer_sentiment)}`}>{a.sentiment?.customer_emotion || a.sentiment?.customer_sentiment || "N/A"}</p>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-3">
                <p className="text-zinc-500 text-[10px] uppercase mb-1">Agent Tone</p>
                <p className="font-bold text-sm capitalize text-[#7000FF]">{a.sentiment?.agent_tone || a.sentiment?.agent_sentiment || "N/A"}</p>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-3">
                <p className="text-zinc-500 text-[10px] uppercase mb-1">Customer</p>
                <p className={`font-bold text-sm capitalize ${sentimentColor(a.sentiment?.customer_sentiment)}`}>{a.sentiment?.customer_sentiment || "N/A"}</p>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-3">
                <p className="text-zinc-500 text-[10px] uppercase mb-1">Agent</p>
                <p className={`font-bold text-sm capitalize ${sentimentColor(a.sentiment?.agent_sentiment)}`}>{a.sentiment?.agent_sentiment || "N/A"}</p>
              </div>
            </div>

            {/* Sentiment Shift */}
            {a.sentiment?.customer_sentiment_shift && (
              <div className="bg-zinc-900/50 rounded-xl p-4">
                <p className="text-zinc-400 text-xs font-medium mb-2">Customer Sentiment Shift</p>
                <div className="flex items-center gap-3 mb-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${sentimentBg(a.sentiment.customer_sentiment_shift.start_sentiment)} ${sentimentColor(a.sentiment.customer_sentiment_shift.start_sentiment)}`}>{a.sentiment.customer_sentiment_shift.start_sentiment}</span>
                  <ArrowRight size={14} className="text-zinc-600" />
                  <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${sentimentBg(a.sentiment.customer_sentiment_shift.end_sentiment)} ${sentimentColor(a.sentiment.customer_sentiment_shift.end_sentiment)}`}>{a.sentiment.customer_sentiment_shift.end_sentiment}</span>
                  <span className={`text-xs font-medium capitalize ml-2 ${a.sentiment.customer_sentiment_shift.direction === "improved" ? "text-emerald-400" : a.sentiment.customer_sentiment_shift.direction === "worsened" ? "text-red-400" : "text-zinc-400"}`}>{a.sentiment.customer_sentiment_shift.direction}</span>
                </div>
                <p className="text-zinc-500 text-xs">{a.sentiment.customer_sentiment_shift.description}</p>
              </div>
            )}

            {/* Escalation Risk */}
            {a.sentiment?.escalation_risk && (
              <div className={`rounded-xl p-4 ${riskBg(a.sentiment.escalation_risk.level)}`}>
                <div className="flex items-center gap-2 mb-2">
                  <AlertOctagon size={16} className={riskColor(a.sentiment.escalation_risk.level)} />
                  <p className={`text-sm font-bold capitalize ${riskColor(a.sentiment.escalation_risk.level)}`}>Escalation Risk: {a.sentiment.escalation_risk.level} ({a.sentiment.escalation_risk.score}/100)</p>
                </div>
                {a.sentiment.escalation_risk.triggers?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {a.sentiment.escalation_risk.triggers.map((t, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 bg-black/20 rounded-full text-zinc-300">"{t}"</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Sentiment Journey Chart */}
            {a.sentiment?.sentiment_journey?.length > 0 && (
              <div className="bg-zinc-900/50 rounded-xl p-4">
                <p className="text-zinc-400 text-xs font-medium mb-1">Sentiment Journey (Score over Time)</p>
                <SentimentJourney journey={a.sentiment.sentiment_journey} />
              </div>
            )}

            {/* Sentiment Segments */}
            {a.sentiment?.segments?.length > 0 && (
              <div>
                <p className="text-zinc-400 text-sm font-medium mb-3">Key Sentiment Moments</p>
                <div className="space-y-2">
                  {a.sentiment.segments.map((seg, i) => (
                    <div key={i} className={`p-3 rounded-lg ${sentimentBg(seg.sentiment)}`}>
                      <p className="text-zinc-200 text-sm italic">"{seg.text}"</p>
                      <p className={`text-xs mt-1 ${sentimentColor(seg.sentiment)}`}>{seg.sentiment} — {seg.reason}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Empathy Effectiveness ── */}
        {activeTab === "empathy" && (
          <div className="space-y-5">
            {a.empathy_effectiveness ? (
              <>
                <div className="flex items-center gap-4">
                  <ScoreRing score={a.empathy_effectiveness.score || 0} label="Empathy Score" size={90} />
                  <div className="flex-1">
                    <p className="text-zinc-200 text-sm">{a.empathy_effectiveness.overall_assessment}</p>
                  </div>
                </div>

                {/* Techniques Used */}
                {a.empathy_effectiveness.empathy_techniques_used?.length > 0 && (
                  <div>
                    <p className="text-zinc-400 text-xs font-medium mb-2">Techniques Used</p>
                    <div className="flex flex-wrap gap-1.5">
                      {a.empathy_effectiveness.empathy_techniques_used.map((t, i) => (
                        <span key={i} className="text-[10px] px-2.5 py-1 bg-emerald-500/10 text-emerald-400 rounded-full">{t}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empathy Moments */}
                {a.empathy_effectiveness.moments?.length > 0 && (
                  <div>
                    <p className="text-zinc-400 text-xs font-medium mb-2">Empathy Moments</p>
                    <div className="space-y-2">
                      {a.empathy_effectiveness.moments.map((m, i) => (
                        <div key={i} className={`p-3 rounded-lg ${m.effectiveness === "high" ? "bg-emerald-500/10 border border-emerald-500/20" : m.effectiveness === "medium" ? "bg-amber-500/10 border border-amber-500/20" : "bg-zinc-800/50 border border-zinc-700"}`}>
                          <p className="text-zinc-200 text-sm italic">"{m.quote}"</p>
                          <p className="text-zinc-500 text-xs mt-1">Customer response: {m.customer_response}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${m.effectiveness === "high" ? "bg-emerald-500/20 text-emerald-400" : m.effectiveness === "medium" ? "bg-amber-500/20 text-amber-400" : "bg-zinc-700 text-zinc-400"}`}>{m.effectiveness}</span>
                            <span className="text-zinc-600 text-[10px]">{m.impact}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Missed Opportunities */}
                {a.empathy_effectiveness.missed_opportunities?.length > 0 && (
                  <div>
                    <p className="text-zinc-400 text-xs font-medium mb-2">Missed Opportunities</p>
                    <div className="space-y-1.5">
                      {a.empathy_effectiveness.missed_opportunities.map((m, i) => (
                        <div key={i} className="flex items-start gap-2 p-2 bg-red-500/5 rounded-lg">
                          <AlertTriangle size={12} className="text-red-400 flex-shrink-0 mt-0.5" />
                          <p className="text-zinc-400 text-xs">{m}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-zinc-500 text-sm">Empathy data not available for this analysis.</p>
            )}
          </div>
        )}

        {/* ── Resolution Satisfaction ── */}
        {activeTab === "resolution" && (
          <div className="space-y-5">
            {a.resolution_satisfaction ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                    <ScoreRing score={a.resolution_satisfaction.resolution_score || 0} label="Resolution" size={70} />
                  </div>
                  <div className="bg-zinc-900/50 rounded-xl p-4">
                    <p className="text-zinc-500 text-[10px] uppercase mb-1">Issue Resolved</p>
                    <p className={`font-bold text-lg ${a.resolution_satisfaction.issue_resolved ? "text-emerald-400" : "text-red-400"}`}>{a.resolution_satisfaction.issue_resolved ? "Yes" : "No"}</p>
                  </div>
                  <div className="bg-zinc-900/50 rounded-xl p-4">
                    <p className="text-zinc-500 text-[10px] uppercase mb-1">Customer Signal</p>
                    <p className={`font-bold text-sm capitalize ${a.resolution_satisfaction.customer_satisfaction_signal === "satisfied" ? "text-emerald-400" : a.resolution_satisfaction.customer_satisfaction_signal === "unsatisfied" ? "text-red-400" : "text-amber-400"}`}>{a.resolution_satisfaction.customer_satisfaction_signal || "N/A"}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-zinc-900/50 rounded-xl p-3">
                    <p className="text-zinc-500 text-[10px] uppercase mb-1">First Call Resolution</p>
                    <p className={`font-bold text-sm ${a.resolution_satisfaction.first_call_resolution ? "text-emerald-400" : "text-amber-400"}`}>{a.resolution_satisfaction.first_call_resolution ? "Yes" : "No"}</p>
                  </div>
                  <div className="bg-zinc-900/50 rounded-xl p-3">
                    <p className="text-zinc-500 text-[10px] uppercase mb-1">Follow-up Needed</p>
                    <p className={`font-bold text-sm ${a.resolution_satisfaction.follow_up_needed ? "text-amber-400" : "text-emerald-400"}`}>{a.resolution_satisfaction.follow_up_needed ? "Yes" : "No"}</p>
                  </div>
                </div>

                {a.resolution_satisfaction.resolution_method && (
                  <div className="bg-zinc-900/50 rounded-xl p-4">
                    <p className="text-zinc-500 text-[10px] uppercase mb-1">Resolution Method</p>
                    <p className="text-zinc-200 text-sm">{a.resolution_satisfaction.resolution_method}</p>
                  </div>
                )}

                {a.resolution_satisfaction.follow_up_details && (
                  <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/20">
                    <p className="text-amber-400 text-xs font-medium mb-1">Follow-up Details</p>
                    <p className="text-zinc-300 text-sm">{a.resolution_satisfaction.follow_up_details}</p>
                  </div>
                )}

                {a.resolution_satisfaction.satisfaction_indicators?.length > 0 && (
                  <div>
                    <p className="text-zinc-400 text-xs font-medium mb-2">Satisfaction Indicators</p>
                    <div className="flex flex-wrap gap-1.5">
                      {a.resolution_satisfaction.satisfaction_indicators.map((s, i) => (
                        <span key={i} className="text-[10px] px-2.5 py-1 bg-zinc-800 text-zinc-300 rounded-full">"{s}"</span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-zinc-500 text-sm">Resolution data not available for this analysis.</p>
            )}
          </div>
        )}

        {/* ── Dead Air ── */}
        {activeTab === "dead_air" && (
          <div className="space-y-5">
            {a.dead_air ? (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                    <p className="text-zinc-500 text-[10px] uppercase mb-1">Total Dead Air</p>
                    <p className="text-white font-bold text-2xl">{a.dead_air.total_estimated_seconds || 0}<span className="text-zinc-500 text-sm font-normal">s</span></p>
                  </div>
                  <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                    <p className="text-zinc-500 text-[10px] uppercase mb-1">Dead Air %</p>
                    <p className="text-white font-bold text-2xl">{a.dead_air.dead_air_percentage || 0}<span className="text-zinc-500 text-sm font-normal">%</span></p>
                  </div>
                  <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                    <p className="text-zinc-500 text-[10px] uppercase mb-1">Instances</p>
                    <p className="text-white font-bold text-2xl">{a.dead_air.instances?.length || 0}</p>
                  </div>
                </div>

                {a.dead_air.assessment && (
                  <div className="bg-zinc-900/50 rounded-xl p-4">
                    <p className="text-zinc-500 text-[10px] uppercase mb-1">Assessment</p>
                    <p className="text-zinc-200 text-sm">{a.dead_air.assessment}</p>
                  </div>
                )}

                {/* Dead Air Timeline Visualization */}
                {a.dead_air.instances?.length > 0 && (
                  <div>
                    <p className="text-zinc-400 text-xs font-medium mb-3">Dead Air Timeline</p>
                    {/* Visual bar */}
                    <div className="relative h-10 bg-zinc-900 rounded-lg overflow-hidden mb-3">
                      <div className="absolute inset-0 bg-emerald-500/10" />
                      {a.dead_air.instances.map((d, i) => {
                        const left = d.timestamp_pct || 0;
                        const width = Math.max(2, Math.min(parseFloat(d.duration_estimate) || 2, 15));
                        const typeColors = { hold: "bg-amber-500/60", system_delay: "bg-blue-500/60", awkward_silence: "bg-red-500/60", thinking: "bg-purple-500/40", note_taking: "bg-zinc-500/40" };
                        return (
                          <div key={i} className={`absolute top-0 bottom-0 ${typeColors[d.type] || "bg-red-500/40"}`}
                            style={{ left: `${left}%`, width: `${width}%` }}
                            title={`${d.type}: ~${d.duration_estimate}s at ${left}%`}
                          />
                        );
                      })}
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {[["hold", "bg-amber-500/60", "Hold"], ["system_delay", "bg-blue-500/60", "System Delay"], ["awkward_silence", "bg-red-500/40", "Silence"], ["thinking", "bg-purple-500/40", "Thinking"], ["note_taking", "bg-zinc-500/40", "Note Taking"]].map(([, bg, label]) => (
                        <span key={label} className="flex items-center gap-1 text-[9px] text-zinc-500"><span className={`w-3 h-2 rounded-sm ${bg} inline-block`} />{label}</span>
                      ))}
                    </div>

                    {/* Instance details */}
                    <div className="space-y-2">
                      {a.dead_air.instances.map((d, i) => (
                        <div key={i} className="flex items-center gap-3 p-2.5 bg-zinc-900/50 rounded-lg">
                          <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                            <Volume2 size={14} className="text-zinc-500" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-white text-xs font-medium">~{d.duration_estimate}s</span>
                              <span className="text-[10px] px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded-full capitalize">{(d.type || "silence").replace("_", " ")}</span>
                              <span className="text-zinc-600 text-[10px]">at {d.timestamp_pct}%</span>
                            </div>
                            <p className="text-zinc-500 text-[10px] mt-0.5">{d.context}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-zinc-500 text-sm">Dead air data not available for this analysis.</p>
            )}
          </div>
        )}


        {/* ── Behavioral Analytics ── */}
        {activeTab === "behavioral" && (
          <div className="space-y-5">
            {a.behavioral_analytics ? (() => {
              const ba = a.behavioral_analytics;
              const tt = ba.talk_time || {};
              const intr = ba.interruptions || {};
              const sp = ba.speech_speed || {};

              return (
                <>
                  {/* Overall Score */}
                  <div className="flex items-center gap-5">
                    <ScoreRing score={ba.overall_behavior_score || 0} label="Behavior Score" size={90} />
                    <div className="flex-1 space-y-2">
                      {(ba.recommendations || []).map((r, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <Sparkles size={12} className="text-[#00F0FF] flex-shrink-0 mt-0.5" />
                          <p className="text-zinc-300 text-xs">{r}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Talk-Time Ratio */}
                  <div className="bg-zinc-900/50 rounded-xl p-4">
                    <p className="text-zinc-400 text-xs font-medium mb-3">Talk-Time Ratio</p>
                    <div className="flex h-8 rounded-lg overflow-hidden mb-3">
                      <div className="bg-[#7000FF]/60 flex items-center justify-center text-white text-[10px] font-bold transition-all" style={{ width: `${tt.agent_percentage || 50}%` }}>
                        Agent {tt.agent_percentage || 50}%
                      </div>
                      {(tt.silence_percentage || 0) > 0 && (
                        <div className="bg-zinc-700/60 flex items-center justify-center text-zinc-400 text-[10px] transition-all" style={{ width: `${tt.silence_percentage}%` }}>
                          {tt.silence_percentage}%
                        </div>
                      )}
                      <div className="bg-[#00F0FF]/40 flex items-center justify-center text-white text-[10px] font-bold transition-all" style={{ width: `${tt.customer_percentage || 50}%` }}>
                        Customer {tt.customer_percentage || 50}%
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <p className="text-[#7000FF] font-bold text-lg">{tt.agent_seconds || 0}<span className="text-zinc-500 text-xs">s</span></p>
                        <p className="text-zinc-500 text-[10px]">Agent</p>
                      </div>
                      <div>
                        <p className="text-zinc-400 font-bold text-lg">{tt.silence_percentage || 0}<span className="text-zinc-500 text-xs">%</span></p>
                        <p className="text-zinc-500 text-[10px]">Silence</p>
                      </div>
                      <div>
                        <p className="text-[#00F0FF] font-bold text-lg">{tt.customer_seconds || 0}<span className="text-zinc-500 text-xs">s</span></p>
                        <p className="text-zinc-500 text-[10px]">Customer</p>
                      </div>
                    </div>
                    {tt.assessment && <p className="text-zinc-500 text-xs mt-3 pt-2 border-t border-zinc-800/50">{tt.assessment}</p>}
                  </div>

                  {/* Interruptions */}
                  <div className="bg-zinc-900/50 rounded-xl p-4">
                    <p className="text-zinc-400 text-xs font-medium mb-3">Interruptions</p>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div className="text-center bg-zinc-800/50 rounded-lg p-3">
                        <p className="text-white font-bold text-2xl">{intr.total_count || 0}</p>
                        <p className="text-zinc-500 text-[10px]">Total</p>
                      </div>
                      <div className="text-center bg-[#7000FF]/10 rounded-lg p-3">
                        <p className="text-[#7000FF] font-bold text-2xl">{intr.agent_interruptions || 0}</p>
                        <p className="text-zinc-500 text-[10px]">By Agent</p>
                      </div>
                      <div className="text-center bg-[#00F0FF]/10 rounded-lg p-3">
                        <p className="text-[#00F0FF] font-bold text-2xl">{intr.customer_interruptions || 0}</p>
                        <p className="text-zinc-500 text-[10px]">By Customer</p>
                      </div>
                    </div>
                    {intr.instances?.length > 0 && (
                      <div className="space-y-1.5">
                        {intr.instances.map((inst, i) => (
                          <div key={i} className={`flex items-start gap-2 p-2 rounded-lg ${inst.impact === "negative" ? "bg-red-500/5" : "bg-zinc-800/30"}`}>
                            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${inst.interrupter === "agent" ? "bg-[#7000FF]/15 text-[#7000FF]" : "bg-[#00F0FF]/15 text-[#00F0FF]"}`}>{inst.interrupter}</span>
                            <p className="text-zinc-400 text-xs flex-1">{inst.context}</p>
                            {inst.impact === "negative" && <span className="text-[9px] text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded-full">negative</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {intr.assessment && <p className="text-zinc-500 text-xs mt-3 pt-2 border-t border-zinc-800/50">{intr.assessment}</p>}
                  </div>

                  {/* Speech Speed */}
                  <div className="bg-zinc-900/50 rounded-xl p-4">
                    <p className="text-zinc-400 text-xs font-medium mb-3">Speech Speed</p>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div className="text-center">
                        <p className="text-[#7000FF] font-bold text-2xl">{sp.agent_wpm || 0}</p>
                        <p className="text-zinc-500 text-[10px]">Agent WPM</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full mt-1 inline-block capitalize ${
                          sp.agent_pace === "slow" ? "bg-blue-500/15 text-blue-400" :
                          sp.agent_pace === "fast" || sp.agent_pace === "very_fast" ? "bg-amber-500/15 text-amber-400" :
                          "bg-zinc-800 text-zinc-400"
                        }`}>{(sp.agent_pace || "moderate").replace("_", " ")}</span>
                      </div>
                      <div className="text-center">
                        <p className="text-[#00F0FF] font-bold text-2xl">{sp.customer_wpm || 0}</p>
                        <p className="text-zinc-500 text-[10px]">Customer WPM</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full mt-1 inline-block capitalize ${
                          sp.customer_pace === "slow" ? "bg-blue-500/15 text-blue-400" :
                          sp.customer_pace === "fast" || sp.customer_pace === "very_fast" ? "bg-amber-500/15 text-amber-400" :
                          "bg-zinc-800 text-zinc-400"
                        }`}>{(sp.customer_pace || "moderate").replace("_", " ")}</span>
                      </div>
                    </div>

                    {/* Speed visualization bar */}
                    <div className="relative h-6 bg-zinc-800 rounded-full overflow-hidden mb-3">
                      <div className="absolute left-0 top-0 bottom-0 bg-gradient-to-r from-blue-500/40 via-emerald-500/40 to-amber-500/40 w-full" />
                      {/* Agent marker */}
                      <div className="absolute top-0 bottom-0 w-0.5 bg-[#7000FF]" style={{ left: `${Math.min(((sp.agent_wpm || 130) / 250) * 100, 100)}%` }}>
                        <div className="absolute -top-3 -translate-x-1/2 text-[8px] text-[#7000FF] font-bold whitespace-nowrap">A</div>
                      </div>
                      {/* Customer marker */}
                      <div className="absolute top-0 bottom-0 w-0.5 bg-[#00F0FF]" style={{ left: `${Math.min(((sp.customer_wpm || 130) / 250) * 100, 100)}%` }}>
                        <div className="absolute -bottom-3 -translate-x-1/2 text-[8px] text-[#00F0FF] font-bold whitespace-nowrap">C</div>
                      </div>
                      {/* Labels */}
                      <div className="absolute left-1 top-1/2 -translate-y-1/2 text-[8px] text-blue-300">Slow</div>
                      <div className="absolute right-1 top-1/2 -translate-y-1/2 text-[8px] text-amber-300">Fast</div>
                    </div>

                    {/* Pace Changes */}
                    {sp.pace_changes?.length > 0 && (
                      <div className="mt-3 space-y-1.5">
                        <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Pace Changes</p>
                        {sp.pace_changes.map((pc, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            <span className={`font-bold capitalize ${pc.who === "agent" ? "text-[#7000FF]" : "text-[#00F0FF]"}`}>{pc.who}</span>
                            <span className="text-zinc-500 capitalize">{(pc.from_pace || "").replace("_", " ")}</span>
                            <ArrowRight size={10} className="text-zinc-600" />
                            <span className="text-zinc-300 capitalize">{(pc.to_pace || "").replace("_", " ")}</span>
                            <span className="text-zinc-600 text-[10px]">({pc.when})</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {sp.assessment && <p className="text-zinc-500 text-xs mt-3 pt-2 border-t border-zinc-800/50">{sp.assessment}</p>}
                  </div>
                </>
              );
            })() : (
              <p className="text-zinc-500 text-sm">Behavioral analytics not available for this analysis.</p>
            )}
          </div>
        )}

        {/* ── Topics ── */}
        {activeTab === "topics" && (
          <div className="space-y-3">
            {(a.topics || []).map((t, i) => (
              <div key={i} className="bg-zinc-900/50 rounded-xl p-4 flex items-start gap-3">
                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${t.relevance === "high" ? "bg-[#00F0FF]" : t.relevance === "medium" ? "bg-amber-400" : "bg-zinc-500"}`} />
                <div>
                  <p className="text-white text-sm font-medium">{t.topic}</p>
                  <p className="text-zinc-500 text-xs mt-0.5">{t.details}</p>
                  <span className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full ${t.relevance === "high" ? "bg-[#00F0FF]/15 text-[#00F0FF]" : "bg-zinc-800 text-zinc-400"}`}>{t.relevance}</span>
                </div>
              </div>
            ))}
            {(!a.topics || a.topics.length === 0) && <p className="text-zinc-500 text-sm">No topics detected.</p>}
          </div>
        )}

        {/* ── Actions ── */}
        {activeTab === "actions" && (
          <div className="space-y-3">
            {(a.action_items || []).map((item, i) => (
              <div key={i} className="bg-zinc-900/50 rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <p className="text-white text-sm font-medium">{item.action}</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${item.priority === "high" ? "bg-red-500/15 text-red-400" : item.priority === "medium" ? "bg-amber-500/15 text-amber-400" : "bg-zinc-800 text-zinc-400"}`}>{item.priority}</span>
                </div>
                <p className="text-zinc-500 text-xs mt-1">Assigned to: <span className="text-zinc-300 capitalize">{item.assignee}</span></p>
              </div>
            ))}
            {(!a.action_items || a.action_items.length === 0) && <p className="text-zinc-500 text-sm">No action items detected.</p>}
          </div>
        )}

        {/* ── Agent Score ── */}
        {activeTab === "agent" && a.agent_score && (
          <div className="space-y-5">
            <div className="flex flex-wrap justify-center gap-6">
              <ScoreRing score={a.agent_score.overall} label="Overall" size={80} />
              <ScoreRing score={a.agent_score.communication} label="Communication" />
              <ScoreRing score={a.agent_score.empathy} label="Empathy" />
              <ScoreRing score={a.agent_score.resolution} label="Resolution" />
              <ScoreRing score={a.agent_score.professionalism} label="Professional" />
            </div>
            {a.agent_score.feedback && (
              <div className="bg-zinc-900/50 rounded-xl p-4">
                <p className="text-zinc-500 text-xs mb-1">AI Feedback</p>
                <p className="text-zinc-200 text-sm">{a.agent_score.feedback}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Main Page ─── */
export default function CallAnalyzerPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [inputMode, setInputMode] = useState("upload"); // upload | record | url
  const [audioFile, setAudioFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [user, setUser] = useState(null);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [showPricing, setShowPricing] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [compareResult, setCompareResult] = useState(null);
  const [comparing, setComparing] = useState(false);
  const fileInputRef = useRef(null);
  const recorder = useAudioRecorder();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    startGoogleLogin('/ai-tools/call-analyzer');
  };

  const handleLogout = async () => {
    const config = getAuthConfig();
    try {
      await axios.post(`${API}/user-auth/logout`, {}, config);
    } catch {}
    sessionStorage.removeItem("user_data");
    setUser(null);
    setUsage(null);
    setSubscription(null);
    axios.get(`${API}/call-analyzer/usage`).then(r => setUsage(r.data)).catch(() => {});
  };

  // Get auth config for API calls - use header-based auth instead of cookies for cross-origin
  const getAuthConfig = useCallback(() => {
    const stored = sessionStorage.getItem("user_data");
    if (!stored) return {};
    try {
      const data = JSON.parse(stored);
      if (data.session_token) {
        return { headers: { Authorization: `Bearer ${data.session_token}` } };
      }
    } catch {}
    return { withCredentials: true };
  }, []);

  const refreshData = () => {
    const config = getAuthConfig();
    axios.get(`${API}/call-analyzer/usage`, config).then(r => setUsage(r.data)).catch((e) => {
      console.warn("Usage fetch failed:", e.message);
      // Fallback: try without credentials
      axios.get(`${API}/call-analyzer/usage`).then(r => setUsage(r.data)).catch(() => {});
    });
    axios.get(`${API}/call-analyzer/history`, config).then(r => setHistory(r.data)).catch(() => {});
    axios.get(`${API}/subscriptions/my-subscription`, config).then(r => setSubscription(r.data)).catch(() => {});
  };

  useEffect(() => {
    // Check for user from auth callback state or sessionStorage
    const stateUser = location.state?.user;
    const storedUser = sessionStorage.getItem("user_data");

    if (stateUser) {
      setUser(stateUser);
    } else if (storedUser) {
      try { setUser(JSON.parse(storedUser)); } catch {}
    }

    // Verify session with backend (use stored token)
    const storedForAuth = sessionStorage.getItem("user_data");
    if (storedForAuth) {
      try {
        const parsed = JSON.parse(storedForAuth);
        if (parsed.session_token) {
          axios.get(`${API}/user-auth/me`, { headers: { Authorization: `Bearer ${parsed.session_token}` } })
            .then(r => { setUser({ ...r.data, session_token: parsed.session_token }); sessionStorage.setItem("user_data", JSON.stringify({ ...r.data, session_token: parsed.session_token })); })
            .catch(() => {});
        }
      } catch {}
    }

    // Fetch plans (no auth needed)
    axios.get(`${API}/subscriptions/plans`).then(r => setPlans(r.data)).catch(() => {});

    // Fetch usage without credentials first (works for anonymous), then refresh with auth
    axios.get(`${API}/call-analyzer/usage`).then(r => setUsage(r.data)).catch(() => {});
    axios.get(`${API}/call-analyzer/history`).then(r => setHistory(r.data)).catch(() => {});
    axios.get(`${API}/subscriptions/my-subscription`).then(r => setSubscription(r.data)).catch(() => {});

    refreshData();

    // Handle subscription payment return
    const subOrder = searchParams.get("sub_order");
    if (subOrder) {
      axios.post(`${API}/subscriptions/verify-payment`, { order_id: subOrder }, getAuthConfig())
        .then(r => {
          if (r.data.status === "paid") {
            refreshData();
          }
        })
        .catch(() => {});
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [location.state, searchParams]);

  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (file) setAudioFile(file);
  };

  const analyzeCall = async () => {
    setAnalyzing(true);
    setError(null);
    setProgress("Uploading audio...");
    setResult(null);

    try {
      const formData = new FormData();

      if (inputMode === "upload" && audioFile) {
        formData.append("audio_file", audioFile);
      } else if (inputMode === "record" && recorder.audioBlob) {
        formData.append("audio_file", recorder.audioBlob, "recording.webm");
      } else if (inputMode === "url" && audioUrl) {
        formData.append("audio_url", audioUrl);
      } else {
        setError("Please provide audio input");
        setAnalyzing(false);
        return;
      }

      // Submit for async processing (returns immediately)
      const authConfig = getAuthConfig();
      const headers = { "Content-Type": "multipart/form-data", ...(authConfig.headers || {}) };
      const res = await axios.post(`${API}/call-analyzer/analyze`, formData, {
        headers,
        timeout: 30000 // 30s for upload only
      });

      const analysisId = res.data.analysis_id;
      if (!analysisId) throw new Error("No analysis ID returned");

      setProgress("Transcribing audio...");

      // Poll for results
      const maxPolls = 60; // 60 * 3s = 3 min max
      for (let i = 0; i < maxPolls; i++) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const statusRes = await axios.get(`${API}/call-analyzer/status/${analysisId}`, { timeout: 10000, ...authConfig });
          const { status, data: resultData, progress: serverProgress, error: serverError } = statusRes.data;

          if (serverProgress) setProgress(serverProgress);

          if (status === "completed" && resultData) {
            setResult(resultData);
            setProgress("");
            refreshData();
            return;
          } else if (status === "failed") {
            setError(serverError || "Analysis failed");
            setProgress("");
            return;
          }
          // still processing, continue polling
        } catch (pollErr) {
          // Network hiccup during polling is ok, retry
          console.warn("Poll error, retrying:", pollErr.message);
        }
      }

      setError("Analysis is taking too long. Check your history in a minute.");
      setProgress("");

    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Analysis failed";
      setError(msg);
      setProgress("");
    } finally {
      setAnalyzing(false);
    }
  };

  const loadFromHistory = async (analysisId) => {
    try {
      const res = await axios.get(`${API}/call-analyzer/history/${analysisId}`);
      setResult(res.data);
      setShowHistory(false);
    } catch {
      setError("Failed to load analysis");
    }
  };

  const deleteFromHistory = async (analysisId) => {
    try {
      await axios.delete(`${API}/call-analyzer/history/${analysisId}`);
      setHistory(h => h.filter(a => a.analysis_id !== analysisId));
      if (result?.analysis_id === analysisId) setResult(null);
    } catch {}
  };

  const formatDuration = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  const canCompare = usage?.is_subscribed && ["professional", "enterprise"].includes(usage?.plan_id);

  const toggleCompareSelect = (analysisId) => {
    setSelectedForCompare(prev => {
      if (prev.includes(analysisId)) return prev.filter(id => id !== analysisId);
      if (prev.length >= 4) return prev;
      return [...prev, analysisId];
    });
  };

  const runComparison = async () => {
    if (selectedForCompare.length < 2) return;
    setComparing(true);
    setCompareResult(null);
    try {
      const res = await axios.post(`${API}/call-analyzer/compare`, { analysis_ids: selectedForCompare }, getAuthConfig());
      setCompareResult(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || "Comparison failed");
    } finally {
      setComparing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-[#27272A]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#00F0FF] rounded-sm flex items-center justify-center"><Brain size={18} className="text-black" /></div>
            <span className="font-outfit font-bold text-[#00F0FF] text-lg tracking-tight">ShansoAI<span className="text-white">Hub</span></span>
          </Link>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/ai-tools")} className="hidden sm:flex items-center gap-2 text-zinc-400 hover:text-white text-sm transition-colors">
              <ArrowLeft size={16} /> AI Tools
            </button>
            {usage && (
              <div className="text-xs text-zinc-500">
                {usage.is_subscribed ? (
                  <span className="text-emerald-400">{usage.plan_id === "enterprise" ? "Unlimited" : `${usage.remaining} calls left`}</span>
                ) : usage.is_logged_in ? (
                  usage.remaining > 0 ? (
                    <span>Free: <span className="text-[#00F0FF] font-bold">{usage.remaining}</span>/{usage.limit} left</span>
                  ) : (
                    <button onClick={() => setShowPricing(true)} className="text-amber-400 hover:text-amber-300 transition-colors">Upgrade</button>
                  )
                ) : usage.remaining > 0 ? (
                  <span>Free: <span className="text-[#00F0FF] font-bold">{usage.remaining}</span>/{usage.limit} left</span>
                ) : (
                  <span className="text-amber-400">Free trial used</span>
                )}
              </div>
            )}
            {user ? (
              <div className="flex items-center gap-2">
                {user.picture && <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-zinc-700" />}
                <span className="text-zinc-300 text-xs hidden sm:inline">{user.name?.split(" ")[0]}</span>
                <button onClick={handleLogout} className="text-zinc-500 hover:text-red-400 transition-colors" title="Logout" data-testid="logout-btn">
                  <LogOut size={16} />
                </button>
              </div>
            ) : (
              <button
                onClick={handleGoogleLogin}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 border border-zinc-700 text-white text-xs rounded-lg hover:bg-white/20 transition-colors"
                data-testid="google-login-btn"
              >
                <svg width="14" height="14" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                Sign in
              </button>
            )}
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-6 max-w-4xl mx-auto">
        {/* Header */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0} className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#00F0FF]/10 border border-[#00F0FF]/20 rounded-full px-4 py-1.5 mb-4">
            <Phone size={14} className="text-[#00F0FF]" />
            <span className="text-[#00F0FF] text-xs font-semibold tracking-wider uppercase">Customer Call Analyzer</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold font-outfit mb-3">
            Analyze Customer Calls with <span className="text-[#00F0FF]">AI</span>
          </h1>
          <p className="text-zinc-400 text-sm sm:text-base max-w-xl mx-auto">
            Upload a call recording and get instant AI-powered insights — sentiment, topics, action items, and agent performance scoring.
          </p>
        </motion.div>

        {/* History Toggle + Compare */}
        {history.length > 0 && (
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={1} className="mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => { setShowHistory(!showHistory); setCompareMode(false); setCompareResult(null); }}
                className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm transition-colors"
                data-testid="toggle-history"
              >
                <History size={16} /> Past Analyses ({history.length})
                {showHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>

              {canCompare && history.length >= 2 && (
                <button
                  onClick={() => { setCompareMode(!compareMode); setShowHistory(true); setSelectedForCompare([]); setCompareResult(null); }}
                  className={`flex items-center gap-1.5 text-sm transition-colors ${compareMode ? "text-[#7000FF] font-semibold" : "text-zinc-500 hover:text-[#7000FF]"}`}
                  data-testid="compare-mode-btn"
                >
                  <BarChart3 size={14} /> Compare Calls
                </button>
              )}
              {!canCompare && history.length >= 2 && user && (
                <span className="text-zinc-600 text-xs flex items-center gap-1">
                  <BarChart3 size={12} /> Compare Calls <span className="text-[#7000FF]">(Pro+)</span>
                </span>
              )}
            </div>

            {/* Compare bar */}
            {compareMode && (
              <div className="mt-3 p-3 bg-[#7000FF]/10 border border-[#7000FF]/20 rounded-xl flex items-center justify-between">
                <p className="text-[#7000FF] text-xs">
                  Select 2-4 analyses to compare ({selectedForCompare.length} selected)
                </p>
                <button
                  onClick={runComparison}
                  disabled={selectedForCompare.length < 2 || comparing}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-[#7000FF] text-white text-xs font-bold rounded-lg disabled:opacity-40"
                  data-testid="run-compare-btn"
                >
                  {comparing ? <Loader2 size={12} className="animate-spin" /> : <BarChart3 size={12} />}
                  Compare
                </button>
              </div>
            )}

            {showHistory && !compareResult && (
              <div className="mt-3 space-y-2">
                {history.map(h => (
                  <div key={h.analysis_id} className={`bg-[#0d0d14] border rounded-xl p-3 flex items-center justify-between ${
                    compareMode && selectedForCompare.includes(h.analysis_id) ? "border-[#7000FF]" : "border-zinc-800/60"
                  }`}>
                    {compareMode && (
                      <button
                        onClick={() => toggleCompareSelect(h.analysis_id)}
                        className={`w-5 h-5 rounded border mr-3 flex items-center justify-center flex-shrink-0 ${
                          selectedForCompare.includes(h.analysis_id) ? "bg-[#7000FF] border-[#7000FF]" : "border-zinc-600"
                        }`}
                        data-testid={`compare-check-${h.analysis_id}`}
                      >
                        {selectedForCompare.includes(h.analysis_id) && <Check size={12} className="text-white" />}
                      </button>
                    )}
                    <div className="flex-1 cursor-pointer" onClick={() => compareMode ? toggleCompareSelect(h.analysis_id) : loadFromHistory(h.analysis_id)}>
                      <p className="text-white text-sm font-medium">{h.filename || "Recording"}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-zinc-500 text-xs">{h.created_at?.split("T")[0]}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          h.days_remaining <= 2 ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"
                        }`}>
                          <Clock size={10} className="inline mr-1" />
                          {h.days_remaining} day{h.days_remaining !== 1 ? "s" : ""} left
                        </span>
                      </div>
                    </div>
                    {!compareMode && (
                      <button onClick={() => deleteFromHistory(h.analysis_id)} className="text-zinc-600 hover:text-red-400 transition-colors p-1">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Compare Results */}
            {compareResult && (
              <div className="mt-4 space-y-4" data-testid="compare-results">
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-bold text-lg font-outfit">Comparison Results</h3>
                  <button onClick={() => { setCompareResult(null); setCompareMode(false); setSelectedForCompare([]); }} className="text-zinc-500 hover:text-white text-xs">Close</button>
                </div>

                {/* Averages */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-[#0d0d14] border border-zinc-800/60 rounded-xl p-4 text-center">
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider">Calls Compared</p>
                    <p className="text-white font-bold text-2xl mt-1">{compareResult.count}</p>
                  </div>
                  <div className="bg-[#0d0d14] border border-zinc-800/60 rounded-xl p-4 text-center">
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider">Avg Sentiment</p>
                    <p className="text-[#00F0FF] font-bold text-2xl mt-1">{compareResult.avg_sentiment_score}</p>
                  </div>
                  <div className="bg-[#0d0d14] border border-zinc-800/60 rounded-xl p-4 text-center">
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider">Avg Agent Score</p>
                    <p className="text-[#7000FF] font-bold text-2xl mt-1">{compareResult.avg_agent_score}</p>
                  </div>
                  <div className="bg-[#0d0d14] border border-zinc-800/60 rounded-xl p-4 text-center">
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider">Total Actions</p>
                    <p className="text-amber-400 font-bold text-2xl mt-1">{compareResult.all_action_items}</p>
                  </div>
                </div>

                {/* Side-by-side cards */}
                <div className={`grid gap-3 ${compareResult.count <= 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-" + Math.min(compareResult.count, 4)}`}>
                  {(compareResult.analyses || []).map((a, i) => {
                    const isBest = a.analysis_id === compareResult.best_agent_score?.id;
                    const isWorst = a.analysis_id === compareResult.worst_agent_score?.id && compareResult.count > 1;
                    return (
                      <div key={a.analysis_id} className={`bg-[#0d0d14] border rounded-xl p-4 ${isBest ? "border-emerald-500/40" : isWorst ? "border-red-500/30" : "border-zinc-800/60"}`}>
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-white text-sm font-medium truncate">{a.filename}</p>
                          {isBest && <span className="text-[10px] bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded-full">Best</span>}
                          {isWorst && compareResult.count > 2 && <span className="text-[10px] bg-red-500/15 text-red-400 px-2 py-0.5 rounded-full">Needs Work</span>}
                        </div>
                        <div className="space-y-2 text-xs">
                          <div className="flex justify-between"><span className="text-zinc-500">Sentiment</span><span className={`font-medium capitalize ${a.sentiment?.overall === "positive" ? "text-emerald-400" : a.sentiment?.overall === "negative" ? "text-red-400" : "text-zinc-300"}`}>{a.sentiment?.overall} ({a.sentiment?.score})</span></div>
                          <div className="flex justify-between"><span className="text-zinc-500">Agent Score</span><span className="text-[#7000FF] font-bold">{a.agent_score?.overall}/100</span></div>
                          <div className="flex justify-between"><span className="text-zinc-500">Communication</span><span className="text-zinc-300">{a.agent_score?.communication}</span></div>
                          <div className="flex justify-between"><span className="text-zinc-500">Empathy</span><span className="text-zinc-300">{a.agent_score?.empathy}</span></div>
                          <div className="flex justify-between"><span className="text-zinc-500">Resolution</span><span className="text-zinc-300">{a.agent_score?.resolution}</span></div>
                          <div className="flex justify-between"><span className="text-zinc-500">Topics</span><span className="text-zinc-300">{a.topics?.length || 0}</span></div>
                          <div className="flex justify-between"><span className="text-zinc-500">Actions</span><span className="text-amber-400">{a.action_items?.length || 0}</span></div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Common Topics */}
                {Object.keys(compareResult.all_topics || {}).length > 0 && (
                  <div className="bg-[#0d0d14] border border-zinc-800/60 rounded-xl p-4">
                    <p className="text-zinc-400 text-xs font-medium mb-2">Common Topics Across Calls</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(compareResult.all_topics).map(([topic, count]) => (
                        <span key={topic} className="text-xs px-3 py-1 bg-zinc-800 text-zinc-300 rounded-full">
                          {topic} <span className="text-[#00F0FF] ml-1">x{count}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}

        {/* Login Gate - show when free trial exhausted and not logged in */}
        {!result && usage && usage.needs_login && !user && (
          <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-8">
            <div className="bg-gradient-to-br from-[#0d0d1a] to-[#0a0a14] border border-amber-500/30 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/10 flex items-center justify-center">
                <LogIn size={28} className="text-amber-400" />
              </div>
              <h3 className="text-white font-bold text-xl font-outfit mb-2">Free Trial Complete</h3>
              <p className="text-zinc-400 text-sm mb-6 max-w-md mx-auto">
                You've used your {usage.limit} free analyses. Sign in with Google to subscribe and get more analyses.
              </p>
              <button
                onClick={handleGoogleLogin}
                className="inline-flex items-center gap-3 px-8 py-3 bg-white text-black font-bold rounded-xl hover:bg-gray-100 transition-colors"
                data-testid="google-login-gate-btn"
              >
                <svg width="20" height="20" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                Continue with Google
              </button>
            </div>
          </motion.div>
        )}

        {/* Subscription Gate - logged in but needs subscription */}
        {!result && user && usage && usage.needs_subscription && (
          <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-8">
            <div className="text-center mb-6">
              <h3 className="text-white font-bold text-xl font-outfit mb-2">Choose Your Plan</h3>
              <p className="text-zinc-400 text-sm">Your free trial is complete. Subscribe to continue analyzing calls.</p>
            </div>
            <SubscriptionPlans
              plans={plans}
              currentPlan={subscription?.plan?.id || "free"}
              user={user}
              onSubscribed={refreshData}
            />
          </motion.div>
        )}

        {/* Pricing Section (toggled from nav) */}
        {showPricing && !result && (
          <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-lg font-outfit">Upgrade Your Plan</h3>
              <button onClick={() => setShowPricing(false)} className="text-zinc-500 hover:text-white"><X size={18} /></button>
            </div>
            <SubscriptionPlans
              plans={plans}
              currentPlan={subscription?.plan?.id || "free"}
              user={user}
              onSubscribed={() => { refreshData(); setShowPricing(false); }}
            />
          </motion.div>
        )}

        {/* Input Section */}
        {!result && (!usage?.needs_login || user) && (!usage?.needs_subscription) && !showPricing && (
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={2}>
            {/* Input Mode Tabs */}
            <div className="flex gap-2 mb-4">
              {[
                { id: "upload", label: "Upload File", icon: Upload },
                { id: "record", label: "Record", icon: Mic },
                { id: "url", label: "From URL", icon: LinkIcon }
              ].map(mode => (
                <button
                  key={mode.id}
                  onClick={() => setInputMode(mode.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    inputMode === mode.id ? "bg-[#00F0FF] text-black" : "bg-[#0d0d14] text-zinc-400 border border-zinc-800 hover:border-zinc-600"
                  }`}
                  data-testid={`input-mode-${mode.id}`}
                >
                  <mode.icon size={16} /> {mode.label}
                </button>
              ))}
            </div>

            {/* Upload Mode */}
            {inputMode === "upload" && (
              <div
                className="border-2 border-dashed border-zinc-700 hover:border-[#00F0FF] rounded-2xl p-10 text-center cursor-pointer transition-colors"
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleFileDrop}
                onDragOver={(e) => e.preventDefault()}
                data-testid="audio-dropzone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".mp3,.wav,.m4a,.webm,.mp4,.mpeg,.mpga"
                  className="hidden"
                  onChange={handleFileDrop}
                />
                {audioFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileAudio size={24} className="text-[#00F0FF]" />
                    <div className="text-left">
                      <p className="text-white text-sm font-medium">{audioFile.name}</p>
                      <p className="text-zinc-500 text-xs">{(audioFile.size / 1024 / 1024).toFixed(1)} MB</p>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); setAudioFile(null); }} className="text-zinc-500 hover:text-red-400 ml-2"><X size={18} /></button>
                  </div>
                ) : (
                  <>
                    <Upload size={32} className="text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-400 text-sm">Drag & drop audio file or <span className="text-[#00F0FF]">click to browse</span></p>
                    <p className="text-zinc-600 text-xs mt-1">MP3, WAV, M4A, WebM (max 25MB)</p>
                  </>
                )}
              </div>
            )}

            {/* Record Mode */}
            {inputMode === "record" && (
              <div className="bg-[#0d0d14] border border-zinc-800/60 rounded-2xl p-8 text-center">
                {recorder.audioBlob ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-center gap-3">
                      <FileAudio size={24} className="text-[#00F0FF]" />
                      <p className="text-white text-sm">Recording — {formatDuration(recorder.duration)}</p>
                      <button onClick={recorder.clearRecording} className="text-zinc-500 hover:text-red-400"><X size={18} /></button>
                    </div>
                    <audio src={URL.createObjectURL(recorder.audioBlob)} controls className="mx-auto" />
                  </div>
                ) : recorder.isRecording ? (
                  <div className="space-y-4">
                    <div className="w-20 h-20 mx-auto rounded-full bg-red-500/20 flex items-center justify-center animate-pulse">
                      <MicOff size={32} className="text-red-400" />
                    </div>
                    <p className="text-red-400 font-bold text-lg">{formatDuration(recorder.duration)}</p>
                    <button onClick={recorder.stopRecording} className="px-6 py-2.5 bg-red-500 text-white rounded-xl font-medium" data-testid="stop-recording">
                      Stop Recording
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="w-20 h-20 mx-auto rounded-full bg-[#00F0FF]/10 flex items-center justify-center">
                      <Mic size={32} className="text-[#00F0FF]" />
                    </div>
                    <p className="text-zinc-400 text-sm">Click to start recording</p>
                    <button onClick={recorder.startRecording} className="px-6 py-2.5 bg-[#00F0FF] text-black rounded-xl font-bold" data-testid="start-recording">
                      Start Recording
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* URL Mode */}
            {inputMode === "url" && (
              <div className="bg-[#0d0d14] border border-zinc-800/60 rounded-2xl p-6">
                <label className="text-zinc-400 text-sm mb-2 block">Audio URL (direct link to MP3, WAV, etc.)</label>
                <input
                  type="url"
                  value={audioUrl}
                  onChange={(e) => setAudioUrl(e.target.value)}
                  placeholder="https://example.com/call-recording.mp3"
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm placeholder-zinc-600 focus:border-[#00F0FF] focus:outline-none"
                  data-testid="audio-url-input"
                />
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3">
                <AlertTriangle size={18} className="text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            {/* Analyze Button */}
            <div className="mt-6 text-center">
              <button
                onClick={analyzeCall}
                disabled={analyzing || (inputMode === "upload" && !audioFile) || (inputMode === "record" && !recorder.audioBlob) || (inputMode === "url" && !audioUrl)}
                className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#00F0FF] to-[#7000FF] text-black font-bold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed text-sm"
                data-testid="analyze-btn"
              >
                {analyzing ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    {progress || "Analyzing..."}
                  </>
                ) : (
                  <>
                    <Sparkles size={18} /> Analyze Call
                  </>
                )}
              </button>
              {analyzing && (
                <p className="text-zinc-500 text-xs mt-3">This may take 30-60 seconds depending on the recording length.</p>
              )}
            </div>
          </motion.div>
        )}

        {/* Results */}
        {result && (
          <motion.div variants={fadeUp} initial="hidden" animate="visible">
            {/* Expiry warning */}
            {result.expires_in_days && (
              <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-center gap-2">
                <Clock size={16} className="text-amber-400" />
                <p className="text-amber-300 text-xs">
                  This analysis will be auto-deleted in <span className="font-bold">{result.expires_in_days} days</span>. Download the report to keep a copy.
                </p>
              </div>
            )}

            <AnalysisResults data={result} />

            <div className="mt-6 text-center">
              <button
                onClick={() => { setResult(null); setAudioFile(null); setAudioUrl(""); recorder.clearRecording(); }}
                className="inline-flex items-center gap-2 px-6 py-2.5 border border-zinc-700 text-zinc-300 rounded-xl hover:border-[#00F0FF] transition-colors text-sm"
                data-testid="new-analysis-btn"
              >
                <Play size={16} /> Analyze Another Call
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
